// deps
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

// internal-deps
import { format, ToggleSwitch } from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import ToolTip, { TooltipElementType } from "@swiggy-private/dls-web-core/dist/components/Tooltip";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import type { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";

// interfaces
import { ISplitBillDineCashRowProps } from "@dineout-payment-success-ui/interfaces";

// constants
import { SPLIT_BILL_ARIA_LABELS } from "@dineout-payment-success-ui/constants";
import { INR_CURRENCY_SYMBOL_TEXT } from "@dineout-payment-success-ui/constants/Constants";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import {
    DineCashInfoAnchor,
    DineCashInfoIconTrigger,
    DineCashLeft,
    DineCashRight,
    DineCashSection,
    DineCashTooltipList,
    DineCashTooltipListItem,
    DineCashTooltipWrap,
    DINE_CASH_DLS_TOOLTIP_CLASS,
} from "./styled";

const SplitBillDineCashRow: React.FC<ISplitBillDineCashRowProps> = ({ dineCashSplit, onSelectionChange }) => {
    const [showDineCashTooltip, setShowDineCashTooltip] = useState(false);
    const [isDineCashSelected, setIsDineCashSelected] = useState<boolean>(dineCashSplit.splitDineCash ?? false);
    const dineCashInfoAnchorRef = useRef<HTMLDivElement>(null);
    const dineCashTooltipWrapRef = useRef<HTMLDivElement>(null);

    const dineCashAmountValue = useMemo(() => Number(dineCashSplit.dineCashAmount || 0), [dineCashSplit.dineCashAmount]);
    const isDineCashEnabled = dineCashAmountValue > 0;
    const dineCashInfoCallouts = dineCashSplit.infoCallouts || [];
    const hasDineCashTooltipContent = dineCashInfoCallouts.length > 0;

    useEffect(() => {
        setIsDineCashSelected(dineCashSplit.splitDineCash ?? false);
    }, [dineCashSplit.splitDineCash]);

    useEffect(() => {
        onSelectionChange({
            dineCashAmount: String(dineCashAmountValue),
            splitDineCash: isDineCashSelected,
        });
    }, [dineCashAmountValue, isDineCashSelected, onSelectionChange]);

    const handleDineCashInfoClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setShowDineCashTooltip((open) => !open);
    }, []);

    const handleDineCashTooltipClose = useCallback(() => {
        setShowDineCashTooltip(false);
    }, []);

    useEffect(() => {
        if (!showDineCashTooltip) {
            return undefined;
        }

        const closeIfOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (target == null) {
                return;
            }
            if (dineCashInfoAnchorRef.current?.contains(target)) {
                return;
            }
            if (dineCashTooltipWrapRef.current?.contains(target)) {
                return;
            }
            if (
                target instanceof Element &&
                target.closest(`.${DINE_CASH_DLS_TOOLTIP_CLASS}`)
            ) {
                return;
            }
            handleDineCashTooltipClose();
        };

        const onEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                handleDineCashTooltipClose();
            }
        };

        document.addEventListener("mousedown", closeIfOutside, true);
        document.addEventListener("touchstart", closeIfOutside, true);
        document.addEventListener("keydown", onEscape, true);

        return () => {
            document.removeEventListener("mousedown", closeIfOutside, true);
            document.removeEventListener("touchstart", closeIfOutside, true);
            document.removeEventListener("keydown", onEscape, true);
        };
    }, [showDineCashTooltip, handleDineCashTooltipClose]);

    const handleDineCashToggleClick = useCallback(() => {
        if (!isDineCashEnabled) {
            return;
        }
        setIsDineCashSelected((selected) => !selected);
    }, [isDineCashEnabled]);

    const dineCashInfoIconProps = useMemo(() => {
        const iconName = (dineCashSplit.iconId) as IconName;

        return {
            iconName,
            fillColor: "text_Med_Emphasis" as const,
            strokeColor: "text_Med_Emphasis" as const,
        };
    }, [dineCashSplit.iconId]);

    const shouldShowDineCashTooltip = showDineCashTooltip && hasDineCashTooltipContent;

    return (
        <DineCashSection isEnabled={isDineCashEnabled} data-testid={SPLIT_BILL_TEST_IDS.DINECASH_ROW}>
            <DineCashLeft>
                <Image cloudinaryId={dineCashSplit.imageId} width="28" height="28" />
                <Text variant="V2.body1.neutral.bold" color="text_Highest_Emphasis">
                    {dineCashSplit.title}
                </Text>
                <DineCashInfoAnchor ref={dineCashInfoAnchorRef}>
                    <DineCashInfoIconTrigger
                        onClick={handleDineCashInfoClick}
                        data-testid={SPLIT_BILL_TEST_IDS.DINECASH_INFO_ICON}
                        role="button"
                        tabIndex={0}
                        aria-expanded={showDineCashTooltip}
                        aria-label={SPLIT_BILL_ARIA_LABELS.DINECASH_INFO}
                    >
                        <SpriteIconWeb {...dineCashInfoIconProps} aria-hidden />
                    </DineCashInfoIconTrigger>
                    {shouldShowDineCashTooltip && (
                            <DineCashTooltipWrap ref={dineCashTooltipWrapRef}>
                                <ToolTip
                                    className={DINE_CASH_DLS_TOOLTIP_CLASS}
                                    direction="down"
                                    elementType={TooltipElementType.LEFT_ASSET_NONE}
                                    onClose={handleDineCashTooltipClose}
                                    testId={SPLIT_BILL_TEST_IDS.DINECASH_DLS_TOOLTIP}
                                    isFullWidth
                                    text=""
                                >
                                    <DineCashTooltipList>
                                        {dineCashInfoCallouts.map((callout) => (
                                            <DineCashTooltipListItem key={callout}>{callout}</DineCashTooltipListItem>
                                        ))}
                                    </DineCashTooltipList>
                                </ToolTip>
                            </DineCashTooltipWrap>
                    )}
                </DineCashInfoAnchor>
            </DineCashLeft>
            <DineCashRight>
                <ToggleSwitch
                    selected={isDineCashSelected}
                    isDisabled={!isDineCashEnabled}
                    onClick={handleDineCashToggleClick}
                    activeColor="special_primary"
                    thumbColor="background_Primary"
                />
                <Text variant="V2.heading2.neutral.bold" color="text_Highest_Emphasis">
                    {format(INR_CURRENCY_SYMBOL_TEXT, String(dineCashAmountValue))}
                </Text>
            </DineCashRight>
        </DineCashSection>
    );
};

export default memo(SplitBillDineCashRow);
